import {useMemo} from 'react';

import {getPage, getSearchFilters, getUrlFilters} from 'shared/lib/filterHelper';
import IOrderBy from 'shared/model/IOrderBy';
import {SetOrderByType} from 'shared/ui/Table/Table';

import {useSearchHandler} from './useSearchParamsHandler';
import {IUseSortOptions, useSort} from './useSort';

export interface IListArgs {
    page: number,
    sort?: IOrderBy,
    filters: string[],
}

export interface IListParams {
    args: IListArgs,
    sort?: IOrderBy,
    filters: string[],
    searchParams: URLSearchParams,
    setSort: SetOrderByType,
    search: string,
    onSearch: (_value: string) => void,
    hasFilters: boolean,
}

interface IUseListParamsOptions extends IUseSortOptions {
    filterKeys?: readonly string[],
    getFilters?: (_params: URLSearchParams) => string[],
}

const NO_FILTER_KEYS: readonly string[] = [];

const useListParams = ({
    filterKeys = NO_FILTER_KEYS,
    getFilters,
    defaultSort,
    sortFields,
    resetPage,
}: IUseListParamsOptions = {}): IListParams => {
    const {searchParams, currentValue, onSearchSubmit} = useSearchHandler();
    const [sort, setSort] = useSort({defaultSort, sortFields, resetPage});
    const page = getPage(searchParams);

    const filters = useMemo(
        () => getFilters
            ? getFilters(searchParams)
            : [...getSearchFilters(searchParams), ...getUrlFilters(searchParams, filterKeys)],
        [getFilters, searchParams, filterKeys],
    );

    const args = useMemo(() => ({page, sort, filters}), [page, sort, filters]);

    return {
        args,
        sort,
        filters,
        searchParams,
        setSort,
        search: currentValue,
        onSearch: onSearchSubmit,
        hasFilters: filters.length > 0,
    };
};

export default useListParams;
